import { select, all, call, takeEvery, put, delay } from 'redux-saga/effects';
import { fetchEntity } from '@classpass/saga-tools';
import { addMonths, startOfDay, format } from 'date-fns';

import { waitForModal, openModal, closeModal } from 'components/Modal';
import { createErrorBanner } from 'components/Banner';
import URLS from 'constants/urls';
import { selectActiveVenueId, selectIaLogic, selectActiveVenue } from 'selectors/general';
import { IA_KEY } from 'helpers/ia-logic';
import { venueClasses, classtivitySchedules } from 'store/saga/entities/bound';
import { transformKeysToSnakeCase } from 'helpers/core';
import { adjustDateForTimezone } from 'helpers/intl-fns';
import { SERVER_DATE_FORMAT, VENUE_SOURCE } from 'constants/logic';
import { reloadPage } from 'helpers/page-utils';
import { waitForAppInit } from 'features/App/App.saga';

import { reportError } from 'helpers/error-logging';
import {
  generateScheduleInstances,
  detectDuplicates,
  mapScheduleInstancesToCreateSchedulesData,
} from './ClasstivitySchedulerForm/ClasstivitySchedulerForm.helpers';
import { setNewScheduleInstances, setNewClassId } from './ClasstivityScheduler.reducers';
import {
  selectNewScheduleInstances,
  selectNewScheduleInstancesLength,
  selectClasstivitySchedules,
} from './ClasstivityScheduler.selectors';
import { sagaMessages as messages } from './ClasstivityScheduler.messages';
import { DEFAULT_VALUES } from './ClasstivitySchedulerForm/ClasstivitySchedulerForm';

export const GENERATE_SCHEDULES = 'CLASSTIVITY_SCHEDULER/GENERATE_SCHEDULES';
export const generateSchedules = (formData, formAsyncFns) => ({
  type: GENERATE_SCHEDULES,
  formData,
  formAsyncFns,
});

export const PUBLISH_SCHEDULES = 'CLASSTIVITY_SCHEDULER/PUBLISH_SCHEDULES';
export const publishSchedules = () => ({ type: PUBLISH_SCHEDULES });

export const CREATE_CLASS = 'CLASSTIVITY_SCHEDULER/CREATE_CLASS';
export const createClass = (formData, formAsyncFns) => ({
  type: CREATE_CLASS,
  formData,
  formAsyncFns,
});

export function* doGenerateSchedules(client, action) {
  const { formData, formAsyncFns } = action;
  const { resolveSubmit, resetForm } = formAsyncFns;

  const { data: existingSchedules } = yield select(selectClasstivitySchedules);
  const newSchedules = generateScheduleInstances(formData);
  const { hasDuplicates, schedules } = yield call(detectDuplicates, existingSchedules, newSchedules);

  if (hasDuplicates) {
    yield put(createErrorBanner(messages.duplicateSchedulesDetected));
  }

  yield put(setNewScheduleInstances(schedules));
  yield call(resolveSubmit);
  yield call(resetForm, DEFAULT_VALUES);
}

export function* doCreateClass(client, action) {
  const { httpClient } = client;
  const { formData, formAsyncFns } = action;
  const { resolveSubmit, rejectSubmit } = formAsyncFns;

  try {
    const venueId = yield select(selectActiveVenueId);
    const endpoint = URLS.venue.createClass(venueId);

    const payload = transformKeysToSnakeCase(formData);

    const response = yield call([httpClient, httpClient.post], endpoint, payload);
    const newClassId = response?.data?.data?.class_id;

    yield put(closeModal());
    yield call(resolveSubmit, messages.createClassSuccess);
    yield call(fetchEntity, venueClasses, [
      client,
      { venueId, onlyActive: false, includeDisabled: false, source: VENUE_SOURCE.CLASSTIVITY },
    ]);
    yield put(setNewClassId(newClassId));
  } catch (error) {
    yield all([put(closeModal()), call(rejectSubmit, messages.createClassError), call(reportError, error)]);
  }
}

export function* doPublishSchedules(client) {
  const { httpClient } = client;
  const venueId = yield select(selectActiveVenueId);

  const newScheduleInstancesCount = yield select(selectNewScheduleInstancesLength);
  const confirmed = yield call(waitForModal, client, 'ConfirmPublishModal', {
    schedulesCount: newScheduleInstancesCount,
  });

  if (!confirmed) return;

  const endpoint = URLS.venue.createClasstivitySchedules(venueId);
  const newScheduleInstances = yield select(selectNewScheduleInstances);
  const newSchedulesData = yield call(mapScheduleInstancesToCreateSchedulesData, newScheduleInstances);

  const payload = transformKeysToSnakeCase({
    schedulesData: newSchedulesData,
    schedulesDataEdit: [],
  });

  const response = yield call([httpClient, httpClient.post], endpoint, payload);
  const waitInMilliseconds = response?.data?.data?.wait_ms;

  yield put(openModal('PublishSuccessModal'));
  yield delay(waitInMilliseconds);
  yield call(reloadPage);
}

export function* handleClasstivitySchedulerRouteEnter(client, action) {
  // The active venue (and its timezone) is only set after app init; reading it earlier yields undefined.
  yield call(waitForAppInit);

  const { venueId: stateVenueId, timezone } = yield select(selectActiveVenue);
  const { venueId: actionVenueId } = action || {};
  const venueId = actionVenueId || stateVenueId;

  // Without a venue, fetching would hit /venue/undefined/* and /venues/undefined/* (404s).
  if (!venueId) return;

  const now = new Date();
  const todayInVenueTimezone = startOfDay(adjustDateForTimezone(now, timezone));
  const endDate = addMonths(todayInVenueTimezone, 6);
  const { featureIsSupported } = yield select(selectIaLogic, IA_KEY.CLASSTIVITY_SCHEDULER, venueId);

  if (!featureIsSupported) {
    return;
  }

  yield all([
    call(fetchEntity, venueClasses, [
      client,
      { venueId, onlyActive: false, includeDisabled: false, source: VENUE_SOURCE.CLASSTIVITY },
    ]),
    call(fetchEntity, classtivitySchedules, [
      client,
      venueId,
      format(todayInVenueTimezone, SERVER_DATE_FORMAT),
      format(endDate, SERVER_DATE_FORMAT),
    ]),
  ]);
}

export function* watchClasstivitySchedulerActions(client) {
  yield takeEvery(PUBLISH_SCHEDULES, doPublishSchedules, client);
  yield takeEvery(GENERATE_SCHEDULES, doGenerateSchedules, client);
  yield takeEvery(CREATE_CLASS, doCreateClass, client);
}
